'use client';
import { Button } from "@/components/ui/button";
import {
  applyQueryToParams,
  categoryNames,
  clearSelectFilters,
  isOpenFilterModal,
  cityNames,
  locationNames,
  onSelectCategories,
  onSelectLocations,
  searchText,
  selectedCategories,
  selectedCities,
  selectedEndDate,
  selectedLocations,
  selectedStartDate,
  setIsOpenModal,
  setSearchText,
  setSelectedEndDate,
  setSelectedStartDate,
  onSelectCities,
} from "../store/event-list.store";
import { DatePicker } from "@/components/ui/date-picker";
import router from "next/router";
import { MultiSelect } from "@/components/multi-select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSignals } from "@preact/signals-react/runtime";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { DialogTrigger } from "@radix-ui/react-dialog";
import { useRouter } from "next/navigation";
import {
  SlidersHorizontal,
  Building2,
  Tickets,
  MapPinHouse,
  Shapes,
  CalendarCheck2,
  CalendarMinus,
} from "lucide-react";

export default function EventFilter() {
  useSignals();
  const router = useRouter();

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      applyQueryToParams(router.push);
    }
  };

  return (
    <Dialog open={isOpenFilterModal.value} onOpenChange={setIsOpenModal}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          className="bg-transparent hover:bg-ocean-blue-3 text-ocean-blue-9 gap-2"
        >
          <SlidersHorizontal className="w-5 h-5" />
          Filters
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[512px]">
        <DialogHeader className="border-b border-stone-neutral-6 p-3 items-center">
          <DialogTitle className="text-sm font-bold m-0">
            Filter events
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-6 flex-1 p-4 overflow-y-auto max-h-[calc(100vh-280px)]">
          <div className="flex flex-col">
            <div className="flex items-center">
              <Tickets className="w-4 h-4 mr-1 text-stone-neutral-11" />
              <span
                className="text-base text-stone-neutral-12 font-bold"
                id="label-name"
              >
                Event name
              </span>
            </div>
            <Input
              className="mt-2"
              placeholder="Filter by event name"
              value={searchText.value}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={handleKeyDown}
              aria-labelledby="label-name"
            />
          </div>
          <div className="flex flex-col">
            <div className="flex items-center">
              <Building2 className="w-4 h-4 mr-1 text-stone-neutral-11" />
              <span
                className="text-base text-stone-neutral-12 font-bold"
                id="label-city"
              >
                Cities
              </span>
            </div>
            <div className="mt-2">
              <MultiSelect
                options={cityNames.value}
                selected={selectedCities.value}
                onChange={onSelectCities}
                placeholder="Filter by cities"
                aria-labelledby="label-city"
              />
            </div>
          </div>
          <div className="flex flex-col">
            <div className="flex items-center">
              <MapPinHouse className="w-4 h-4 mr-1 text-stone-neutral-11" />
              <span
                className="text-base text-stone-neutral-12 font-bold"
                id="label-venue"
              >
                Venue
              </span>
            </div>
            <div className="mt-2">
              <MultiSelect
                options={locationNames.value}
                selected={selectedLocations.value}
                onChange={onSelectLocations}
                placeholder="Filter by Venues"
                aria-labelledby="label-venue"
              />
            </div>
          </div>
          <div className="flex flex-col">
            <div className="flex items-center">
              <Shapes className="w-4 h-4 mr-1 text-stone-neutral-11" />
              <span
                className="text-base text-stone-neutral-12 font-bold"
                id="label-category"
              >
                Categories
              </span>
            </div>
            <div className="mt-2">
              <MultiSelect
                options={categoryNames.value}
                selected={selectedCategories.value}
                onChange={onSelectCategories}
                placeholder="Filter by categories"
                aria-labelledby="label-category"
              />
            </div>
          </div>
          <div className="flex flex-col">
            <div className="flex items-center">
              <CalendarCheck2 className="w-4 h-4 mr-1 text-stone-neutral-11" />
              <span
                className="text-base text-stone-neutral-12 font-bold"
                id="label-start-date"
              >
                Event start date
              </span>
            </div>
            <DatePicker
              date={selectedStartDate.value}
              setDate={setSelectedStartDate}
              label=""
              aria-labelledby="label-start-date"
            />
          </div>
          <div className="flex flex-col">
            <div className="flex items-center">
              <CalendarMinus className="w-4 h-4 mr-1 text-stone-neutral-11" />
              <span
                className="text-base text-stone-neutral-12 font-bold"
                id="label-end-date"
              >
                Event end date
              </span>
            </div>
            <DatePicker
              date={selectedEndDate.value}
              setDate={setSelectedEndDate}
              label=""
              aria-labelledby="label-end-date"
            />
          </div>
        </div>
        <DialogFooter className="flex sm:justify-between gap-2 space-x-2 px-4 py-3 border-t border-stone-neutral-6">
          <Button
            variant="secondary"
            onClick={() => clearSelectFilters(router.push)}
            className="hover:bg-ocean-blue-3 text-navy-blue"
          >
            Clear all
          </Button>
          <Button
            onClick={() => applyQueryToParams(router.push)}
            variant="default"
            className="bg-ocean-blue-9 hover:bg-ocean-blue-10"
          >
            Show filtered events
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
